import { repositoryFactory } from '@data';
import { tagMasteryCalculator } from './TagMasteryCalculator.js';

/**
 * Spaced Review Selector Service
 * Handles periodic injection of review questions from earlier sections
 * Prevents forgetting curve decay and maintains knowledge from early course sections
 * 
 * Uses spaced repetition algorithm to:
 * - Identify sections with decayed mastery
 * - Calculate optimal injection timing
 * - Select review questions from early sections
 * - Integrate seamlessly with linear progression
 */
export class SpacedReviewSelector {
  constructor() {
    this.progressRepository = repositoryFactory.getProgressRepository();
    this.courseRepository = repositoryFactory.getCourseRepository();
  }

  /**
   * Get eligible sections for spaced review
   * Filters to sections before current position to avoid future content
   */
  async getEligibleReviewSections(
    userId,
    courseId,
    currentSectionIndex,
    maxSectionsBack = null
  ) {
    const decayedSections = await tagMasteryCalculator.getSectionsWithDecayedMastery(
      userId,
      courseId
    );

    if (decayedSections.length === 0) {
      return [];
    }

    // Filter to only sections before current (avoid future content)
    let eligible = decayedSections.filter(s => s.sectionIndex < currentSectionIndex);

    // Limit how far back to look (don't inject from section 1 when at section 50)
    if (maxSectionsBack) {
      const minIndex = Math.max(0, currentSectionIndex - maxSectionsBack);
      eligible = eligible.filter(s => s.sectionIndex >= minIndex);
    }

    return eligible;
  }

  /**
   * Select review section based on decay priority
   * Returns section with best spaced review characteristics
   */
  async selectReviewSection(
    userId,
    courseId,
    currentSectionIndex,
    maxSectionsBack = 15 // Review sections up to 15 sections back
  ) {
    const eligible = await this.getEligibleReviewSections(
      userId,
      courseId,
      currentSectionIndex,
      maxSectionsBack
    );

    if (eligible.length === 0) {
      return null;
    }

    // Top section is already sorted by decay priority
    return eligible[0];
  }

  /**
   * Get review questions from a section
   * Returns subset of questions from the section for reinforcement
   */
  async getReviewQuestionsFromSection(
    userId,
    courseId,
    sectionId,
    questionCount = 2
  ) {
    const course = await this.courseRepository.getCourseById(courseId);
    if (!course) return [];

    const progress = await this.progressRepository.getUserProgress(userId, courseId);
    if (!progress) return [];

    // Get all questions from section
    const sectionQuestions = await this.courseRepository.getQuestionsForSection(
      courseId,
      sectionId
    );

    if (sectionQuestions.length === 0) {
      return [];
    }

    // Sort by performance (worst performing first - those most needing review)
    const questionsWithScore = sectionQuestions.map(q => {
      const qProgress = progress.getQuestionProgress(q.id);
      const correctRate = qProgress && qProgress.timesShown > 0
        ? qProgress.timesCorrect / qProgress.timesShown
        : 0;
      return {
        question: q,
        correctRate,
        timesShown: qProgress?.timesShown || 0,
      };
    });

    // Sort by: (1) correctRate (lowest first), (2) timesShown (most exposure first)
    questionsWithScore.sort((a, b) => {
      if (Math.abs(a.correctRate - b.correctRate) > 0.01) {
        return a.correctRate - b.correctRate; // Lower score = review first
      }
      return b.timesShown - a.timesShown; // More exposure = review first
    });

    // Return top N questions
    return questionsWithScore
      .slice(0, questionCount)
      .map(item => ({
        ...item.question,
        correctRate: item.correctRate,
        reviewReason: 'spaced_repetition',
      }));
  }

  /**
   * Evaluate if spaced review should be injected now
   * Considers: questions since last review, decay, course length, etc.
   */
  async shouldInjectSpacedReviewNow(
    userId,
    courseId,
    currentSectionIndex,
    sessionId = null
  ) {
    const course = await this.courseRepository.getCourseById(courseId);
    if (!course) return false;

    // Get eligible review sections
    const eligibleSections = await this.getEligibleReviewSections(
      userId,
      courseId,
      currentSectionIndex,
      15
    );

    if (eligibleSections.length === 0) {
      return false; // No sections eligible for review
    }

    // Get best review section
    const reviewSection = eligibleSections[0];

    // Questions since last review (from session if available)
    let questionsSinceLast = 0;
    if (sessionId) {
      try {
        const session = await repositoryFactory.getSessionRepository().getSessionById(sessionId);
        if (session && session.recentAnswerHistory) {
          let recent = [];
          try {
            recent = typeof session.recentAnswerHistory === 'string'
              ? JSON.parse(session.recentAnswerHistory)
              : session.recentAnswerHistory;
          } catch (e) {
            recent = [];
          }
          questionsSinceLast = recent.length;
        }
      } catch (err) {
        console.warn('[SpacedReviewSelector] Error getting session:', err);
      }
    }

    // Calculate injection probability
    const shouldInject = tagMasteryCalculator.shouldInjectSpacedReviewNow(
      userId,
      courseId,
      currentSectionIndex,
      questionsSinceLast,
      reviewSection.avgDecayFactor / 100,
      course.sections.length
    );

    if (shouldInject) {
      console.log('[SpacedReviewSelector] Will inject spaced review:', {
        reviewSection: reviewSection.sectionTitle,
        decay: reviewSection.avgFinalMastery + '%',
      });
    }

    return shouldInject && reviewSection;
  }

  /**
   * Prepare spaced review injection
   * Returns everything needed to inject review questions into flow
   */
  async prepareSpacedReviewInjection(
    userId,
    courseId,
    currentSectionIndex,
    reviewQuestionCount = 2,
    sessionId = null
  ) {
    // Check if should inject
    const reviewSection = await this.shouldInjectSpacedReviewNow(
      userId,
      courseId,
      currentSectionIndex,
      sessionId
    );

    if (!reviewSection) {
      return null;
    }

    // Get review questions
    const reviewQuestions = await this.getReviewQuestionsFromSection(
      userId,
      courseId,
      reviewSection.sectionId,
      reviewQuestionCount
    );

    if (reviewQuestions.length === 0) {
      return null;
    }

    return {
      type: 'spaced_review',
      reviewSection: {
        id: reviewSection.sectionId,
        title: reviewSection.sectionTitle,
        index: reviewSection.sectionIndex,
        mastery: reviewSection.avgFinalMastery + '%',
        decay: (100 - reviewSection.avgDecayFactor) + '% forgotten',
      },
      questions: reviewQuestions,
      questionCount: reviewQuestions.length,
      message: `Quick review: You've forgotten some concepts from "${reviewSection.sectionTitle}". Let's refresh.`,
      resumeToSection: currentSectionIndex,
    };
  }

  /**
   * Log spaced review statistics for debugging
   */
  async logReviewStatistics(userId, courseId) {
    const course = await this.courseRepository.getCourseById(courseId);
    if (!course) return;

    const allSections = await tagMasteryCalculator.getSectionsWithDecayedMastery(
      userId,
      courseId,
      course
    );

    console.log(`[SpacedReviewSelector] Review statistics for ${userId}/${courseId}:`);
    console.log(`  Total sections: ${course.sections.length}`);
    console.log(`  Sections with decay: ${allSections.length}`);
    
    allSections.slice(0, 5).forEach((section, i) => {
      console.log(`  ${i + 1}. ${section.sectionTitle}: ${section.avgFinalMastery}% mastery (${100 - section.avgDecayFactor}% decayed)`);
    });

    return allSections;
  }
}

// Export singleton instance
export const spacedReviewSelector = new SpacedReviewSelector();
